import { useState } from 'react';
import { hotTopics } from '@/mocks/gossip';

interface NavbarProps {
  onSearch: (q: string) => void;
}

const navItems = [
  { label: '今日热榜', href: '#hot' },
  { label: '吃瓜时间线', href: '#timeline' },
  { label: '事件盘点', href: '#events' },
  { label: '年度汇总', href: '#annual' },
  { label: '翻车合集', href: '#flip' },
  { label: '常见问题', href: '#faq' },
];

export default function Navbar({ onSearch }: NavbarProps) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const submitSearch = () => {
    onSearch(keyword.trim());
    setSearchOpen(false);
    document.getElementById('events')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200/70">
      <div className="container-site flex items-center justify-between h-16 md:h-[72px]">
        {/* 品牌 */}
        <a href="#top" className="flex items-center gap-2 whitespace-nowrap">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-fire-fill text-lg"></i>
          </span>
          <span className="leading-none">
            <span className="block font-heading text-xl tracking-wide text-primary-700">吃瓜黑料</span>
            <span className="block text-[11px] text-foreground-500 tracking-widest">娱乐热点事件盘点</span>
          </span>
        </a>

        {/* 桌面导航 */}
        <nav className="hidden md:flex items-center gap-7">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              {item.label}
            </a>
          ))}
        </nav>

        {/* 搜索 + 行动 */}
        <div className="hidden lg:flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
              placeholder="搜索事件 / 明星 / 品牌"
              className="w-56 h-10 pl-4 pr-10 rounded-full bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/60 transition"
            />
            <button onClick={submitSearch} aria-label="搜索" className="absolute right-1 top-1 w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600">
              <i className="ri-search-line text-sm"></i>
            </button>
          </div>
          <a href="#faq" className="inline-flex items-center h-10 px-4 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition whitespace-nowrap">
            点开全知道
          </a>
        </div>

        {/* 移动端按钮 */}
        <div className="flex items-center gap-2 lg:hidden">
          <button onClick={() => setSearchOpen((v) => !v)} aria-label="搜索" className="w-10 h-10 rounded-lg flex items-center justify-center text-foreground-700 hover:bg-background-100">
            <i className="ri-search-line text-xl"></i>
          </button>
          <button onClick={() => setOpen((v) => !v)} aria-label="菜单" className="w-10 h-10 rounded-lg flex items-center justify-center text-foreground-800 hover:bg-background-100">
            <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {/* 移动搜索 */}
      {searchOpen && (
        <div className="lg:hidden px-4 pb-3 flex gap-2">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
            placeholder="搜索事件 / 明星 / 品牌"
            className="flex-1 h-10 px-4 rounded-full bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-400"
          />
          <button onClick={submitSearch} className="h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm">搜索</button>
        </div>
      )}

      {/* 移动菜单 */}
      {open && (
        <nav className="lg:hidden border-t border-background-200/70 bg-background-50 px-4 py-3 flex flex-col gap-1">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100">
              {item.label}
            </a>
          ))}
        </nav>
      )}

      {/* 顶部跑马灯 */}
      <div className="border-t border-background-200/60 bg-accent-500 text-foreground-950 overflow-hidden">
        <div className="flex items-center animate-marquee whitespace-nowrap py-1.5">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {hotTopics.map((t, i) => (
                <span key={`${dup}-${t.rank}`} className="flex items-center text-xs font-medium px-5">
                  <i className="ri-flashlight-fill text-primary-700 mr-1"></i>
                  {t.title}
                  <span className="ml-2 text-foreground-600">{t.heat}</span>
                  <span className="mx-5 text-foreground-400">|</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </header>
  );
}